import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card, CardContent,
  CardActions, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, LinearProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Badge, Divider, FormControlLabel, Switch,
} from '@mui/material';
import {
  Policy as PolicyIcon, Add, Edit, Delete, Visibility, CheckCircle,
  Close, Archive, Publish, People,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_CFG: Record<string, { color: any; label: string }> = {
  draft:    { color: 'default', label: 'Draft' },
  active:   { color: 'success', label: 'Active' },
  archived: { color: 'warning', label: 'Archived' },
};

const CAT_CFG: Record<string, { emoji: string; label: string; color: string }> = {
  hr:         { emoji: '👥', label: 'HR',         color: '#1a237e' },
  security:   { emoji: '🔒', label: 'Security',   color: '#c62828' },
  finance:    { emoji: '💰', label: 'Finance',    color: '#1b5e20' },
  operations: { emoji: '⚙️', label: 'Operations', color: '#e65100' },
  compliance: { emoji: '⚖️', label: 'Compliance', color: '#4a148c' },
  it:         { emoji: '💻', label: 'IT',         color: '#006064' },
  health:     { emoji: '❤️', label: 'Health',     color: '#880e4f' },
  other:      { emoji: '📋', label: 'Other',      color: '#37474f' },
};

interface Policy { id: string; title: string; version?: string; category: string; status: string; summary?: string; content?: string; effectiveDate?: string; reviewDate?: string; audience?: string; requiresAcknowledgement?: boolean; acknowledgementDeadlineDays?: number; acknowledgements?: any[]; myAck?: { acknowledgedAt?: string } | null; }
interface Stats { total: number; active: number; draft: number; archived: number; totalAcks: number; completedAcks: number; }

function PolicyCard({ policy, onView, onAck, onEdit, onPublish, onArchive, onDelete, isHR }: any) {
  const cat = CAT_CFG[policy.category] ?? CAT_CFG.other;
  const acked = !!policy.myAck?.acknowledgedAt;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', border: acked ? '2px solid #2e7d32' : '1px solid #e0e0e0', position: 'relative' }}>
      {acked && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Chip icon={<CheckCircle />} label="Acknowledged" size="small" color="success" />
        </Box>
      )}
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, pr: acked ? 14 : 0 }}>
          <Typography fontSize={22}>{cat.emoji}</Typography>
          <Box>
            <Typography fontWeight={700} variant="body1" sx={{ lineHeight: 1.2 }}>{policy.title}</Typography>
            {policy.version && <Typography variant="caption" color="text.secondary">{policy.version}</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          <Chip label={cat.label} size="small" sx={{ bgcolor: cat.color, color: '#fff' }} />
          <Chip label={STATUS_CFG[policy.status]?.label} size="small" color={STATUS_CFG[policy.status]?.color} />
          {policy.requiresAcknowledgement && <Chip label="Sign-off required" size="small" variant="outlined" />}
        </Box>
        {policy.summary && <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{policy.summary}</Typography>}
        {policy.effectiveDate && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Effective: {new Date(policy.effectiveDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, gap: 0.5 }}>
        <Button size="small" startIcon={<Visibility />} onClick={() => onView(policy)}>Read</Button>
        {policy.requiresAcknowledgement && !acked && policy.status === 'active' && (
          <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => onAck(policy.id)}>
            Sign Off
          </Button>
        )}
        {isHR && <>
          <IconButton size="small" onClick={() => onEdit(policy)}><Edit fontSize="small" /></IconButton>
          {policy.status === 'draft' && <Tooltip title="Publish"><IconButton size="small" color="success" onClick={() => onPublish(policy.id)}><Publish fontSize="small" /></IconButton></Tooltip>}
          {policy.status === 'active' && <Tooltip title="Archive"><IconButton size="small" color="warning" onClick={() => onArchive(policy.id)}><Archive fontSize="small" /></IconButton></Tooltip>}
          {policy.status !== 'active' && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => onDelete(policy.id)}><Delete fontSize="small" /></IconButton></Tooltip>}
        </>}
      </CardActions>
    </Card>
  );
}

export default function Policies() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');

  const [tab, setTab] = useState(0);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [acks, setAcks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [viewDialog, setViewDialog] = useState<Policy | null>(null);
  const [editDialog, setEditDialog] = useState<Partial<Policy> | null>(null);
  const [acksDialog, setAcksDialog] = useState<Policy | null>(null);
  const [acksData, setAcksData] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [dash, st] = await Promise.all([
        apiFetch('/api/policies/my-dashboard'),
        apiFetch('/api/policies/stats'),
      ]);
      setPolicies(dash || []); setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const ack = async (id: string) => {
    try { await apiFetch(`/api/policies/${id}/acknowledge`, { method: 'POST' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const publish = async (id: string) => {
    try { await apiFetch(`/api/policies/${id}/publish`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const archive = async (id: string) => {
    try { await apiFetch(`/api/policies/${id}/archive`, { method: 'PATCH' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const deletePolicy = async (id: string) => {
    if (!window.confirm('Delete this policy?')) return;
    try { await apiFetch(`/api/policies/${id}`, { method: 'DELETE' }); load(); }
    catch (e: any) { setError(e.message); }
  };

  const save = async () => {
    if (!editDialog) return;
    try {
      editDialog.id
        ? await apiFetch(`/api/policies/${editDialog.id}`, { method: 'PATCH', body: JSON.stringify(editDialog) })
        : await apiFetch('/api/policies', { method: 'POST', body: JSON.stringify(editDialog) });
      setEditDialog(null); load();
    } catch (e: any) { setError(e.message); }
  };

  const viewAcks = async (p: Policy) => {
    setAcksDialog(p);
    const data = await apiFetch(`/api/policies/${p.id}/acknowledgements`).catch(() => []);
    setAcksData(data);
  };

  const displayPolicies = catFilter ? policies.filter(p => p.category === catFilter) : policies;
  const pendingAck = policies.filter(p => p.status === 'active' && p.requiresAcknowledgement && !p.myAck?.acknowledgedAt);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PolicyIcon sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Company Policies</Typography>
            <Typography variant="body2" color="text.secondary">Employee handbook & digital acknowledgements</Typography>
          </Box>
        </Box>
        {isHR && (
          <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: '#1a237e' }}
            onClick={() => setEditDialog({ status: 'draft', category: 'hr', requiresAcknowledgement: true, acknowledgementDeadlineDays: 7, audience: 'all' })}>
            New Policy
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Pending acknowledgement banner */}
      {pendingAck.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ⚠️ You have <b>{pendingAck.length} polic{pendingAck.length === 1 ? 'y' : 'ies'}</b> requiring your sign-off: {pendingAck.map(p => p.title).join(', ')}
        </Alert>
      )}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#2e7d32">{stats.active}</Typography>
                <Typography variant="caption" color="text.secondary">Active Policies</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#e65100">{stats.draft}</Typography>
                <Typography variant="caption" color="text.secondary">Drafts</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#1a237e">{stats.completedAcks}</Typography>
                <Typography variant="caption" color="text.secondary">Acknowledgements</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Card sx={{ textAlign: 'center', p: 1 }}>
              <CardContent sx={{ pb: '8px !important' }}>
                <Typography variant="h4" fontWeight={700} color="#4a148c">{pendingAck.length}</Typography>
                <Typography variant="caption" color="text.secondary">Your Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Category filter */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip label="All" onClick={() => setCatFilter('')} color={!catFilter ? 'primary' : 'default'} variant={!catFilter ? 'filled' : 'outlined'} />
        {Object.entries(CAT_CFG).map(([k, v]) => (
          <Chip key={k} label={`${v.emoji} ${v.label}`} onClick={() => setCatFilter(k)}
            color={catFilter === k ? 'primary' : 'default'} variant={catFilter === k ? 'filled' : 'outlined'} />
        ))}
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {displayPolicies.map(p => (
          <Grid key={p.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <PolicyCard
              policy={p}
              onView={setViewDialog}
              onAck={ack}
              onEdit={setEditDialog}
              onPublish={publish}
              onArchive={archive}
              onDelete={deletePolicy}
              isHR={isHR}
            />
          </Grid>
        ))}
        {displayPolicies.length === 0 && !loading && (
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No policies found.</Paper>
          </Grid>
        )}
      </Grid>

      {/* ── VIEW DIALOG ── */}
      <Dialog open={!!viewDialog} onClose={() => setViewDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography fontSize={22}>{CAT_CFG[viewDialog?.category ?? '']?.emoji}</Typography>
            <Box>
              <Typography fontWeight={700}>{viewDialog?.title}</Typography>
              {viewDialog?.version && <Typography variant="caption" color="text.secondary">{viewDialog.version}</Typography>}
            </Box>
          </Box>
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setViewDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={STATUS_CFG[viewDialog?.status ?? '']?.label} size="small" color={STATUS_CFG[viewDialog?.status ?? '']?.color} />
            {viewDialog?.effectiveDate && <Chip label={`Effective ${new Date(viewDialog.effectiveDate + 'T00:00:00').toLocaleDateString('en-HK', { day: 'numeric', month: 'short', year: 'numeric' })}`} size="small" variant="outlined" />}
            {viewDialog?.requiresAcknowledgement && <Chip label="Sign-off required" size="small" variant="outlined" />}
          </Box>
          {viewDialog?.summary && <Typography color="text.secondary" sx={{ mb: 2 }}>{viewDialog.summary}</Typography>}
          <Divider sx={{ mb: 2 }} />
          {viewDialog?.content
            ? <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>{viewDialog.content}</Typography>
            : <Typography color="text.secondary" fontStyle="italic">Full policy document not available. Contact HR for the complete document.</Typography>
          }
          {viewDialog?.myAck?.acknowledgedAt && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1 }}>
              <Typography variant="body2" color="success.main">
                ✅ You acknowledged this policy on {new Date(viewDialog.myAck.acknowledgedAt).toLocaleDateString('en-HK', { day: 'numeric', month: 'long', year: 'numeric' })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewDialog?.requiresAcknowledgement && !viewDialog?.myAck?.acknowledgedAt && viewDialog?.status === 'active' && (
            <Button variant="contained" color="success" startIcon={<CheckCircle />}
              onClick={() => { ack(viewDialog!.id); setViewDialog(null); }}>
              I've Read & Acknowledge This Policy
            </Button>
          )}
          {isHR && (
            <Button startIcon={<People />} onClick={() => { viewAcks(viewDialog!); setViewDialog(null); }}>
              View Acknowledgements
            </Button>
          )}
          <Button onClick={() => setViewDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT DIALOG ── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editDialog?.id ? 'Edit Policy' : 'New Policy'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setEditDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title" fullWidth required value={editDialog?.title ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, title: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Version" sx={{ width: 120 }} value={editDialog?.version ?? ''}
                onChange={e => setEditDialog(p => ({ ...p, version: e.target.value }))} />
              <TextField label="Category" select fullWidth value={editDialog?.category ?? 'hr'}
                onChange={e => setEditDialog(p => ({ ...p, category: e.target.value }))}>
                {Object.entries(CAT_CFG).map(([v, c]) => <MenuItem key={v} value={v}>{c.emoji} {c.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField label="Summary" fullWidth multiline rows={2} value={editDialog?.summary ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, summary: e.target.value }))} />
            <TextField label="Full Content (markdown supported)" fullWidth multiline rows={6} value={editDialog?.content ?? ''}
              onChange={e => setEditDialog(p => ({ ...p, content: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Effective Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={editDialog?.effectiveDate ?? ''} onChange={e => setEditDialog(p => ({ ...p, effectiveDate: e.target.value }))} />
              <TextField label="Review Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={editDialog?.reviewDate ?? ''} onChange={e => setEditDialog(p => ({ ...p, reviewDate: e.target.value }))} />
            </Box>
            <FormControlLabel control={<Switch checked={editDialog?.requiresAcknowledgement ?? true}
              onChange={e => setEditDialog(p => ({ ...p, requiresAcknowledgement: e.target.checked }))} />}
              label="Requires Employee Acknowledgement" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!editDialog?.title}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── ACKNOWLEDGEMENTS DIALOG ── */}
      <Dialog open={!!acksDialog} onClose={() => setAcksDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Acknowledgements — {acksDialog?.title}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setAcksDialog(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Employee</b></TableCell>
                  <TableCell><b>Acknowledged At</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {acksData.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.employee?.firstName} {a.employee?.lastName}</TableCell>
                    <TableCell>
                      {a.acknowledgedAt
                        ? <Chip icon={<CheckCircle />} label={new Date(a.acknowledgedAt).toLocaleDateString('en-HK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} size="small" color="success" />
                        : <Chip label="Pending" size="small" color="warning" />}
                    </TableCell>
                  </TableRow>
                ))}
                {acksData.length === 0 && <TableRow><TableCell colSpan={2} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No acknowledgements yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions><Button onClick={() => setAcksDialog(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
